import { useState, useRef, useEffect } from 'react';
import {
	Box,
	Flex,
	Input,
	Popover,
	PopoverTrigger,
	PopoverContent,
	PopoverBody,
	Text,
} from '@chakra-ui/react';

interface HexColorPickerProps {
	value: string;
	onChange: (hex: string) => void;
	swatchSize?: string;
}

const isValidHex = (v: string) => /^#[0-9A-Fa-f]{6}$/.test(v);

const HexColorPicker = ({ value, onChange, swatchSize = '36px' }: HexColorPickerProps) => {
	const nativeRef = useRef<HTMLInputElement>(null);
	const [hexInput, setHexInput] = useState(value || '#333333');

	useEffect(() => {
		setHexInput(value || '#333333');
	}, [value]);

	const handleHexChange = (raw: string) => {
		setHexInput(raw);
		const v = raw.startsWith('#') ? raw : `#${raw}`;
		if (isValidHex(v)) {
			onChange(v);
		}
	};

	return (
		<Popover placement="bottom-start">
			<PopoverTrigger>
				<Box
					w={swatchSize}
					h={swatchSize}
					borderRadius="md"
					border="2px solid"
					borderColor="gray.300"
					bg={value || '#333333'}
					cursor="pointer"
					flexShrink={0}
				/>
			</PopoverTrigger>
			<PopoverContent w="220px">
				<PopoverBody p={3}>
					<Flex direction="column" gap={3}>
						{/* Hex input */}
						<Flex align="center" gap={2}>
							<Text fontSize="xs" fontWeight="bold" color="gray.500" w="30px">
								HEX
							</Text>
							<Input
								size="sm"
								fontFamily="mono"
								fontSize="sm"
								value={hexInput}
								placeholder="#000000"
								maxLength={7}
								onChange={(e) => handleHexChange(e.target.value)}
							/>
						</Flex>

						{/* Visual picker trigger */}
						<Flex align="center" gap={2}>
							<Text fontSize="xs" color="gray.400">
								Visual picker:
							</Text>
							<Box
								as="label"
								position="relative"
								w="100%"
								h="32px"
								borderRadius="md"
								overflow="hidden"
								cursor="pointer"
								bg={`linear-gradient(to right,
									#ff0000, #ff8000, #ffff00, #00ff00,
									#00ffff, #0000ff, #8000ff, #ff00ff, #ff0000)`}
							>
								<input
									ref={nativeRef}
									type="color"
									value={value || '#333333'}
									onChange={(e) => onChange(e.target.value)}
									style={{
										position: 'absolute',
										opacity: 0,
										width: '100%',
										height: '100%',
										cursor: 'pointer',
										border: 'none',
										padding: 0,
									}}
								/>
							</Box>
						</Flex>
					</Flex>
				</PopoverBody>
			</PopoverContent>
		</Popover>
	);
};

export default HexColorPicker;
